import type { BaseLocationRadiusConfig } from '../LocationSearch.types';

/**
 * Returns the search radius in miles based on the place type/s.
 * Different location types (street, city, state, etc.) require different search radius.
 *
 * Supports both Google Places types and Radar layers.
 *
 * @see https://developers.google.com/maps/documentation/places/web-service/supported_types
 * @see https://radar.com/documentation/api#autocomplete
 */
export function getSearchRadiusByPlaceType(
  types: readonly string[] | string[] | undefined,
  radiusConfig: BaseLocationRadiusConfig,
): number {
  const hasNoTypes = !types || types.length === 0;

  // ─────────────────────────────────────────────────────────────────────────────

  if (hasNoTypes) {
    return radiusConfig.SMALL;
  }

  // Very specific locations: addresses, establishments, premises

  if (
    types.includes('address') ||
    types.includes('place') ||
    types.includes('fine') ||
    types.includes('establishment') ||
    types.includes('street_address') ||
    types.includes('premise') ||
    types.includes('subpremise')
  ) {
    return radiusConfig.SMALLEST;
  }

  // City/neighborhood level: postal codes, neighborhoods

  if (
    types.includes('postalCode') ||
    types.includes('coarse') ||
    types.includes('neighborhood') ||
    types.includes('sublocality') ||
    types.includes('sublocality_level_1')
  ) {
    return radiusConfig.SMALL;
  }

  // City level: localities, postal codes

  if (
    types.includes('locality') ||
    types.includes('postal_code') ||
    types.includes('administrative_area_level_2')
  ) {
    return radiusConfig.MEDIUM;
  }

  // County level: counties, administrative areas

  if (types.includes('county')) {
    return radiusConfig.LARGE;
  }

  // State/country level: states, countries, administrative areas

  if (
    types.includes('state') ||
    types.includes('country') ||
    types.includes('administrative_area_level_1')
  ) {
    return radiusConfig.XLARGE;
  }

  // ─────────────────────────────────────────────────────────────────────────────

  return radiusConfig.SMALL;
}
